import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import { company } from '@/mocks/company';

const heroImg = '/assets/klob-hero-about.jpg';
const aboutImg1 = '/assets/klob-about-01.jpg';
const aboutImg2 = '/assets/klob-about-02.jpg';

const values = [
  { icon: 'ri-focus-3-line', title: 'Präzise', text: 'Saubere Ausführung bis ins Detail — von der Kante bis zur fertigen Oberfläche.' },
  { icon: 'ri-time-line', title: 'Zuverlässig', text: 'Termintreu, erreichbar und ehrlich in der Kommunikation. Kein Rätselraten.' },
  { icon: 'ri-user-heart-line', title: 'Persönlich', text: 'Direkte Betreuung durch einen festen Ansprechpartner vom Start bis zum Ende.' },
  { icon: 'ri-home-4-line', title: 'Bodenständig', text: 'Ein Handwerksbetrieb aus der Region — mit kurzen Wegen und klaren Worten.' },
];

const stats = [
  { value: '100 %', label: 'Fachgerechte Ausführung' },
  { value: `${company.rating}`, label: 'Bewertung bei Google' },
  { value: '6', label: 'Kernleistungen' },
  { value: '1', label: 'Fester Ansprechpartner' },
];

export default function UeberUns() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-50 font-sans">
      <Header />
      <main>
        <PageHero
          eyebrow="Über uns"
          title={<>Handwerk mit <span className="text-primary-500">Anspruch.</span></>}
          subtitle={`Die ${company.name} steht für saubere, zuverlässige und fachgerechte Ausführung — vom ersten Gespräch bis zur fertigen Oberfläche.`}
          image={heroImg}
        />

        <section className="bg-background-50 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-sm border border-primary-500/40"></div>
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
                <img src={aboutImg1} alt="Handwerker bei der Arbeit" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-6 -right-3 sm:-right-6 rounded-sm bg-primary-500 px-6 py-5 text-center">
                <p className="font-display text-3xl font-bold text-background-50">{company.rating}</p>
                <p className="text-[11px] uppercase tracking-widest text-background-50/90">bei Google</p>
              </div>
            </div>
            <div>
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Unser Betrieb</span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-foreground-50">
                Ein Handwerksbetrieb aus <span className="text-primary-500">Hollfeld</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-foreground-100/80">
                Wir sind ein regional verankerter Betrieb für Putz-, Maler- und Ausbauarbeiten. Unsere Arbeit beginnt mit einem
                klaren Gespräch und endet mit einer Oberfläche, die man gerne ansieht.
              </p>
              <p className="mt-4 leading-relaxed text-foreground-400">
                Individuelle Betreuung und ein ehrlicher, bodenständiger Anspruch an unser Handwerk prägen jedes Projekt. Wir
                arbeiten mit bewährten Materialien, planen realistisch und halten, was wir zusagen.
              </p>
              <p className="mt-4 leading-relaxed text-foreground-400">
                Ob Neubau, Sanierung oder Modernisierung — wir stimmen die einzelnen Gewerke aufeinander ab und übergeben ein
                Ergebnis, das dauerhaft Bestand hat.
              </p>
              <Link
                to="/kontakt"
                className="mt-8 inline-flex items-center h-12 px-7 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                Kontakt aufnehmen <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-y border-background-300/40 py-24 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Werte</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Wofür wir <span className="text-primary-500">stehen</span>
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-sm border border-background-300/60 bg-background-50/40 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/50"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className={`${v.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-foreground-50">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-400">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-24 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Zahlen & Fakten</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Klar, ehrlich, <span className="text-primary-500">nachvollziehbar</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-foreground-100/80">
                Wir reden nicht um den heißen Brei. Sie erhalten ein nachvollziehbares Angebot, einen realistischen Zeitplan und
                eine saubere Übergabe.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-4">
                {stats.map((s) => (
                  <div key={s.label} className="rounded-sm border border-background-300/60 bg-background-100 px-5 py-6">
                    <p className="font-display text-3xl font-bold text-primary-300">{s.value}</p>
                    <p className="mt-1 text-sm text-foreground-400">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-background-300/60">
              <img src={aboutImg2} alt="Handwerksdetail" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </section>

        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}